/**
 * The redesign's illustration catalogue: section scenes, giraffe sprites and
 * small elements under src/assets/illustrations. URLs only; `no-inline` keeps
 * the scenes as files instead of data URIs inside whichever chunk imports
 * this, so a page pays for the one scene it shows.
 */

export const SCENE_SECTIONS = [
  'empathy',
  'circles',
  'course',
  'progress',
  'tools',
  'games',
  'landing',
] as const;
export type SceneSection = (typeof SCENE_SECTIONS)[number];
export type SceneVariant = 'hero' | 'mobile';

export const MASCOT_NAMES = [
  'cheer',
  'glasses',
  'hug',
  'laugh',
  'listen',
  'mug',
  'peek',
  'peekleft',
  'read',
  'rest',
  'sit',
  'sleepy',
  'teach',
  'think',
  'walk',
  'wave',
] as const;
export type MascotName = (typeof MASCOT_NAMES)[number];

/** Intrinsic size of each 1x sprite, so the box is reserved before it loads. */
export const MASCOT_SIZE: Record<MascotName, { width: number; height: number }> = {
  cheer: { width: 169, height: 184 },
  glasses: { width: 173, height: 171 },
  hug: { width: 141, height: 226 },
  laugh: { width: 187, height: 171 },
  listen: { width: 191, height: 218 },
  mug: { width: 164, height: 221 },
  peek: { width: 187, height: 170 },
  peekleft: { width: 150, height: 206 },
  read: { width: 184, height: 224 },
  rest: { width: 219, height: 147 },
  sit: { width: 146, height: 185 },
  sleepy: { width: 141, height: 180 },
  teach: { width: 170, height: 232 },
  think: { width: 198, height: 216 },
  walk: { width: 171, height: 185 },
  wave: { width: 198, height: 171 },
};

export const ELEMENT_NAMES = [
  'cloud',
  'flower',
  'hill',
  'leaf-sprig',
  'mountain',
  'sparkle',
  'sun',
] as const;
export type ElementName = (typeof ELEMENT_NAMES)[number];

const sceneFiles = import.meta.glob<string>('../assets/illustrations/scenes/*.svg', {
  eager: true,
  query: '?url&no-inline',
  import: 'default',
});
const mascotFiles = import.meta.glob<string>('../assets/illustrations/mascot/*.webp', {
  eager: true,
  query: '?url&no-inline',
  import: 'default',
});
const elementFiles = import.meta.glob<string>('../assets/illustrations/elements/*.svg', {
  eager: true,
  query: '?url&no-inline',
  import: 'default',
});

function pick(files: Record<string, string>, file: string): string {
  const url = files[`../assets/illustrations/${file}`];
  if (!url) throw new Error(`Missing illustration: ${file}`);
  return url;
}

export function sceneUrl(section: SceneSection, variant: SceneVariant): string {
  return pick(sceneFiles, `scenes/${section}-${variant}.svg`);
}

/** Half size is the 1x image, full size the 2x. */
export function mascotUrls(name: MascotName): { x1: string; x2: string } {
  return {
    x1: pick(mascotFiles, `mascot/${name}.webp`),
    x2: pick(mascotFiles, `mascot/${name}@2x.webp`),
  };
}

export function elementUrl(name: ElementName): string {
  return pick(elementFiles, `elements/${name}.svg`);
}
